import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useSearchParams} from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import Header from './components/Header';
import APIHeader from './components/APIHeader';
import LoginScreen from './screens/login/LoginScreen';
import RegisterScreen from './screens/login/RegisterScreen';
import VerifyEmailPage from './screens/login/VerifyScreen';
import Password from './screens/login/Password';
import { useAuthContext } from './hooks/useAuthContext';
import NotAllowed from './screens/login/NotAllowed';
import Success from './screens/login/Success';
import PasswordReset from './screens/login/PasswordReset';
import jwtDecode from 'jwt-decode';
import usePendoPageTracking from './hooks/usePendoTracking';
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://da10e82ec3aa1765b66882f7d8454f67@o4508455885012992.ingest.us.sentry.io/4508455986790400",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/^https:\/\/app.lucidustech\.com/], 
  //"localhost:3000" ^ add to this if you want to track localhost errs...
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const MainApp = lazy(() => import('./mainApp/MainApp'));
const APIApp = lazy(() => import('./apiApp/APIApp'));

function RoutesWrapper() {
  const { user } = useAuthContext();
  const location = useLocation();
  const decoded = user ? jwtDecode(user.token) : null;
  function isLocalhost() {
    return window.location.href.startsWith('http://localhost:3000');
  }
  
  function isHealthcareSafely() {
    return window.location.href.startsWith('https://app.healthcaresafely.com');
  }

  function isCodeFour() {
    return window.location.href.startsWith('https://app.codefour.ai');
  }
  
  function isLucidusTech() {
    return (
      window.location.href.startsWith('https://app.lucidustech.com') ||
      window.location.href.startsWith('https://stage.lucidustech.com')
    );
  }
  

  if ((!decoded || (!decoded.isAPIUser && !decoded.isHealthCareUser)) && location.pathname.startsWith('/api')) {
    return <Navigate to="/" replace />;
  }

  if (decoded && decoded.isAPIUser && !location.pathname.startsWith('/api')) {
    return <Navigate to="/api" replace />;
  }


  return (
    <Routes>
      {(isLocalhost() || isLucidusTech() || isCodeFour()) && <Route path='/login' element={<LoginScreen />} />}
      {(isLocalhost() || isLucidusTech()) && <Route path='/register' element={<RegisterScreen />} />}
      {(isLocalhost() || isLucidusTech()) && <Route path='/verify' element={<VerifyEmailPage />} />}
      {(isLocalhost() || isLucidusTech() || isCodeFour()) && <Route path='/setup' element={<Password />} />}
      {(isLocalhost() || isLucidusTech()) && <Route path='/register/notallowed' element={<NotAllowed />} />}
      {(isLocalhost() || isLucidusTech()) && <Route path='/register/success' element={<Success />} />}
      {(isLocalhost() || isLucidusTech() || isCodeFour()) && <Route path='/reset' element={<PasswordReset />} />}
      {user && (decoded.isAPIUser ? <Route path='/api*' element={<APIApp />} /> : <Route path='/*' element={<MainApp />} />)}
    </Routes>
  );
}

function PendoTracker() {
  usePendoPageTracking()
  return null
}

function App() {
  const { loading } = useAuthContext();

  function RenderHeader() {
    const location = useLocation();
    const shouldHideHeader = [
      '/login', 
      '/register', 
      '/verify', 
      '/setup', 
      '/register/notallowed', 
      '/register/success', 
      '/reset',
    ].some(path => location.pathname.startsWith(path)) || location.pathname.startsWith('/comms/analyze/');

    
    if (shouldHideHeader) {
      return null; // Do not render the header
    }
  
    if (location.pathname.startsWith('/api')) {
      return <APIHeader />; // Render the API header for /api routes
    }
  
    return <Header />; // Render the default header for other routes
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '10vh' }}>
        <Spinner animation="grow" size="lg" className='loadSpinner' />
      </div>
    );
  }

  // capture all onClick events, log the URL and the text... from the onClicks...
  function GlobalClickListener() {
    const { user } = useAuthContext();
      useEffect(() => {
        const currentUrl = window.location.href; // Get current URL
        if(currentUrl.includes('localhost')) return; // Ignore localhost

        if(!user) return;
        const handleGlobalClick = (event) => {
          if(event.type !== 'click') return; 

          // check if the clicked element has an `onclick` handler
          if (typeof event.target.onclick !== "function") {
            return;
          }

          // console.log(event.target);
          
          const clickedElement = event.target;
          const elementId = clickedElement.id.toLowerCase() || "[No ID]"; 
          // Ensure pendo is available globally
          if (window.pendo && typeof window.pendo.track === "function") {
            window.pendo.track(elementId, {
              User: user.email,
            });
          } else {
            console.warn("Pendo is not initialized");
          }
        };
      
        // Attach event listener
        document.addEventListener("click", handleGlobalClick);
      
        // Cleanup event listener
        return () => {
          document.removeEventListener("click", handleGlobalClick);
        };
      }, []);
    return null;
  }

  return (
    <Router className="App">
      <GlobalClickListener />
     
      <PendoTracker />
      <RenderHeader />
      <main className="py-3">
        <Container>
          <Suspense fallback={<Spinner animation="grow" size='lg' className='loadSpinner'/>}>
            <RoutesWrapper />
          </Suspense>
        </Container>
      </main>
    </Router>
  );
}

export default App;